import type { DonationRequest } from '../types';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X, Mic } from 'lucide-react';
import { useProfileSettings } from '../hooks/useProfileSettings';
import { useAuth } from '../hooks/useAuth';
import { doc, setDoc, addDoc, collection, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { REQUESTS_COLLECTION } from '../config/firebase';
import { PageHeader } from '../components/PageHeader';
import { ImageUploader } from '../components/ImageUploader';
import { useCurrency } from '../hooks/useCurrency';
import { prepareRequestData } from '../utils/migrationUtils';

interface RequestFormData {
  title: string;
  details: string;
  amount: number;
  country: string;
  city: string;
  images: string[];
  audioUrl?: string;
  isPublic: boolean;
  link: string;
  mediaType: 'images' | 'audio';
  hasFixedAmount: boolean;
  isLinkPublic: boolean;
  minDonationAmount: number;
}

export function EditRequest() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const { settings } = useProfileSettings();
  const currency = useCurrency();
  const [formData, setFormData] = useState<RequestFormData>({
    title: '',
    details: '',
    amount: 0,
    country: settings.country,
    city: settings.city,
    images: [],
    audioUrl: undefined,
    isPublic: true,
    link: '',
    mediaType: 'images',
    hasFixedAmount: true,
    isLinkPublic: true,
    minDonationAmount: 100,
  });
  const [audioError, setAudioError] = useState<string | null>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const savedRequests = localStorage.getItem('2dr_requests');
    if (savedRequests) {
      const requests: DonationRequest[] = JSON.parse(savedRequests);
      const request = requests.find(r => r.id === id);
      if (request) {
        if (request.donations && request.donations.length > 0) {
          navigate(`/2dr/${id}`, { 
            state: { error: 'Cette demande a déjà reçu des dons et ne peut plus être modifiée.' }
          });
          return;
        }
        setFormData(prev => ({
          ...prev,
          ...request,
          country: settings.country,
          city: settings.city
        }));
      }
    }
  }, [id, settings]);

  useEffect(() => {
    const checkAccess = async () => {
      console.log('Vérification des droits d\'accès...');
      
      // Vérifier si les données utilisateur sont dans le localStorage
      const storedUserData = localStorage.getItem('user_data');
      const localUser = storedUserData ? JSON.parse(storedUserData) : null;
      
      if (!user && !localUser) {
        console.log('Utilisateur non connecté, redirection vers login');
        navigate('/login');
        return;
      }

      const currentUser = user || localUser;
      
      if (!id) {
        console.log('Pas d\'ID de demande fourni');
        return;
      }

      console.log('ID de la demande:', id);
      console.log('Utilisateur connecté:', currentUser);

      try {
        // D'abord, charger les données de la demande
        const requestDoc = await getDoc(doc(db, REQUESTS_COLLECTION, id));
        console.log('Document trouvé:', requestDoc.exists());
        
        if (!requestDoc.exists()) {
          console.error('Demande non trouvée');
          navigate('/2dr');
          return;
        }

        const requestData = requestDoc.data();
        console.log('Données de la demande:', requestData);
        console.log('ID utilisateur de la demande:', requestData.userId);
        console.log('ID utilisateur connecté:', currentUser.id);
        
        // Vérifier si l'utilisateur est le propriétaire
        if (requestData.userId !== currentUser.id) {
          console.error('Non autorisé à modifier cette demande');
          console.log('ID propriétaire:', requestData.userId);
          console.log('ID utilisateur:', currentUser.id);
          navigate('/2dr');
          return;
        }

        // Vérifier si la demande a des dons
        if (requestData.donations && requestData.donations.length > 0) {
          console.log('La demande a déjà des dons');
          navigate(`/2dr/request/${id}`, { 
            state: { error: 'Cette demande ne peut plus être modifiée car elle a déjà reçu des dons.' }
          });
          return;
        }

        console.log('Chargement des données dans le formulaire...');
        // Si tout est ok, charger les données dans le formulaire
        setFormData(prev => ({
          ...prev,
          ...requestData,
          country: settings.country,
          city: settings.city
        }));
        console.log('Données chargées avec succès');

      } catch (error) {
        console.error('Erreur détaillée lors de la vérification des droits:', error);
        navigate('/2dr');
      }
    };

    checkAccess();
  }, [id, user, navigate, settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (!user) throw new Error('Utilisateur non connecté');

      const initialRequestData = {
        ...formData,
        userId: user.id,
        username: user.username || user.name,
        country: settings.country,
        city: settings.city,
        updatedAt: new Date()
      };

      // Utiliser prepareRequestData pour nettoyer les données
      const requestData = prepareRequestData(initialRequestData);

      if (id) {
        await setDoc(doc(db, REQUESTS_COLLECTION, id), requestData, { merge: true });
      } else {
        await addDoc(collection(db, REQUESTS_COLLECTION), requestData);
      }

      navigate('/2dr');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  const removeImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleAudioUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const audioUrl = URL.createObjectURL(file);
      const audio = new Audio(audioUrl);
      
      // Attendre que les métadonnées soient chargées pour vérifier la durée
      await new Promise((resolve, reject) => {
        audio.addEventListener('loadedmetadata', resolve);
        audio.addEventListener('error', reject);
      });

      if (audio.duration > 60) {
        setAudioError("L'audio ne doit pas dépasser 60 secondes. Veuillez choisir un fichier plus court.");
        if (audioInputRef.current) {
          audioInputRef.current.value = '';
        }
        return;
      }

      setFormData(prev => ({
        ...prev,
        audioUrl: audioUrl
      }));
      setAudioError(null);
    } catch (error) {
      setAudioError(error instanceof Error ? error.message : "Erreur lors du chargement de l'audio");
      if (audioInputRef.current) {
        audioInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="min-h-screen pb-20">
      <PageHeader title="Créez, Recevez, Donnez ! " />
      <audio ref={previewAudioRef} className="hidden" />
      <div className="container mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-white mb-2">Titre</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData(prev => ({ ...prev, title: e.target.value.slice(0, 50) }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500/50"
              required
              maxLength={50}
            />
            <p className="text-sm text-gray-400 mt-1">{formData.title.length}/50 caractères</p>
          </div>

          <div>
            <label className="block text-white mb-2">Détails</label>
            <textarea
              value={formData.details}
              onChange={e => setFormData(prev => ({ ...prev, details: e.target.value.slice(0, 1500) }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500/50 min-h-[150px]"
              required
              maxLength={1500}
            />
            <p className="text-sm text-gray-400 mt-1">{formData.details.length}/1500 caractères</p>
          </div>

          <div className="flex items-center justify-between mb-4">
            <span className="text-white">Définir un montant souhaité</span>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ 
                ...prev, 
                hasFixedAmount: !prev.hasFixedAmount,
                amount: !prev.hasFixedAmount ? prev.amount : 0
              }))}
              className={`w-12 h-6 rounded-full transition-colors relative ${
                formData.hasFixedAmount ? 'bg-green-500' : 'bg-gray-600'
              }`}
            >
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                formData.hasFixedAmount ? 'right-1' : 'left-1'
              }`} />
            </button>
          </div>

          {formData.hasFixedAmount && (
            <div className="space-y-4">
              <div>
                <label className="block text-white mb-2">Montant souhaité ({currency})</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={formData.amount}
                  onChange={e => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setFormData(prev => ({ ...prev, amount: value ? Number(value) : 0 }));
                  }}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500/50"
                  placeholder="Entrez un montant"
                  required={formData.hasFixedAmount}
                />
              </div>

              <div>
                <label className="block text-white mb-2">Montant minimum ({currency})</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={formData.minDonationAmount}
                  onChange={e => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setFormData(prev => ({ ...prev, minDonationAmount: value ? Number(value) : 100 }));
                  }}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500/50"
                  placeholder="Montant minimum par donation"
                  required
                />
                <p className="text-sm text-gray-400 mt-1">Le minimum que les donateurs (trices) devront respecter</p>
              </div>
            </div>
          )}

          {formData.mediaType === 'audio' ? (
            <div className="space-y-2">
              <label className="block text-white mb-2">Audio</label>
              {formData.audioUrl ? (
                <div className="relative bg-white/5 p-4 rounded-xl">
                  <audio
                    src={formData.audioUrl}
                    controls
                    className="w-full"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, audioUrl: undefined }));
                      if (audioInputRef.current) {
                        audioInputRef.current.value = '';
                      }
                    }}
                    className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ) : (
                <label className="w-full h-24 bg-white/5 border border-white/10 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-white/10 transition-colors">
                  <Mic className="w-6 h-6 text-gray-400 mb-2" />
                  <span className="text-gray-400 text-sm">Cliquez pour ajouter un audio</span>
                  <input
                    type="file"
                    accept="audio/*"
                    onChange={handleAudioUpload}
                    ref={audioInputRef}
                    className="hidden"
                  />
                </label>
              )}
              {audioError && (
                <p className="text-red-400 text-sm">{audioError}</p>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-white mb-2">Images</label>
              <div className="grid grid-cols-3 gap-4">
                {formData.images.map((image, index) => (
                  <div key={index} className="relative">
                    <img
                      src={image}
                      alt={`Upload ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ))}
                {formData.images.length < 4 && (
                  <ImageUploader
                    onImageCropped={(croppedImage) => {
                      setFormData(prev => ({
                        ...prev,
                        images: [...prev.images, croppedImage]
                      }));
                    }}
                    aspectRatio={16/9}
                  />
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-white">Mode public</span>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, isPublic: !prev.isPublic }))}
              className={`w-12 h-6 rounded-full transition-colors relative ${
                formData.isPublic ? 'bg-green-500' : 'bg-gray-600'
              }`}
            >
              <div
                className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                  formData.isPublic ? 'right-1' : 'left-1'
                }`}
              />
            </button>
          </div>

          <div>
            <label className="block text-white mb-2">Lien externe (optionnel)</label>
            <input
              type="url"
              value={formData.link}
              onChange={e => setFormData(prev => ({ ...prev, link: e.target.value }))}
              placeholder="https://..."
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500/50"
            />
            {formData.link && (
              <div className="flex items-center justify-between mt-2">
                <span className="text-white">Lien accessible à tous</span>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ 
                    ...prev, 
                    isLinkPublic: !prev.isLinkPublic 
                  }))}
                  className={`w-12 h-6 rounded-full transition-colors relative ${
                    formData.isLinkPublic ? 'bg-green-500' : 'bg-gray-600'
                  }`}
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    formData.isLinkPublic ? 'right-1' : 'left-1'
                  }`} />
                </button>
              </div>
            )}
          </div>

          <div className="space-y-2 mt-4">
            <p className="text-red-400 text-sm">
              Important : Une fois que votre publication aura reçu des dons, elle ne pourra plus être modifiée ou supprimée
            </p>
          </div>

          <button
            type="submit"
            className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-4 rounded-xl transition-colors"
          >
            Mettre à jour la publication
          </button>
        </form>
      </div>
    </div>
  );
}